import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, MapPin, CheckCircle2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Container, Eyebrow, Section, Reveal } from "./common";
import { VERTIENTES } from "../data/content";
import { api, formatApiErrorDetail } from "../lib/api";

export const Contacto = () => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ nombre: "", correo: "", organizacion: "", vertiente: "", mensaje: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vertiente) {
      toast.error("Selecciona una vertiente de interés");
      return;
    }
    setLoading(true);
    try {
      await api.post("/diagnosticos", form);
      setSent(true);
      toast.success("Solicitud enviada", { description: "Respondemos en máximo 48 horas hábiles." });
      setForm({ nombre: "", correo: "", organizacion: "", vertiente: "", mensaje: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="contacto" alt>
      <Container>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <Eyebrow>Hablemos</Eyebrow>
            <h2 className="mt-5 font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-100 leading-tight">
              El primer paso siempre es <span className="text-rc-sage italic">gratuito.</span>
            </h2>
            <p className="mt-6 text-stone-400 font-light leading-relaxed">
              El diagnóstico de 72 horas nos permite entender tu municipio, institución o negocio antes de
              cualquier propuesta. Sin compromiso. Con un entregable real que te llevas.
            </p>

            <div className="mt-10 space-y-4">
              <a href="mailto:raiz.commerce2030@gmail.com" data-testid="contacto-correo"
                className="flex items-center gap-4 p-5 rounded-2xl bg-rc-card border border-rc-border hover:border-rc-sage/40 transition-colors">
                <Mail className="w-5 h-5 text-rc-sage" />
                <div>
                  <div className="text-xs font-mono text-stone-500 uppercase tracking-wider">Correo electrónico</div>
                  <div className="text-stone-200">raiz.commerce2030@gmail.com</div>
                </div>
              </a>
              <a href="https://wa.me/522214125123" target="_blank" rel="noopener noreferrer" data-testid="contacto-whatsapp"
                className="flex items-center gap-4 p-5 rounded-2xl bg-rc-card border border-rc-border hover:border-rc-sage/40 transition-colors">
                <MessageCircle className="w-5 h-5 text-rc-sage" />
                <div>
                  <div className="text-xs font-mono text-stone-500 uppercase tracking-wider">WhatsApp directo</div>
                  <div className="text-stone-200">+52 221 412 5123</div>
                </div>
              </a>
              <div className="flex items-center gap-4 p-5 rounded-2xl bg-rc-card border border-rc-border">
                <MapPin className="w-5 h-5 text-rc-sage" />
                <div>
                  <div className="text-xs font-mono text-stone-500 uppercase tracking-wider">Sede</div>
                  <div className="text-stone-200">Xalapa, Veracruz, México</div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="p-8 md:p-10 rounded-2xl bg-rc-card border border-rc-border">
              {sent ? (
                <div className="flex flex-col items-start gap-4 py-10" data-testid="contacto-confirmacion">
                  <CheckCircle2 className="w-12 h-12 text-rc-sage" strokeWidth={1.4} />
                  <h3 className="font-serif text-2xl text-stone-100">Recibimos tu solicitud</h3>
                  <p className="text-stone-400 font-light leading-relaxed">
                    Gracias por escribirnos. Respondemos en máximo 48 horas hábiles. El diagnóstico inicial no
                    tiene costo ni genera ningún compromiso.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contacto-nueva-solicitud"
                    className="mt-2 text-sm text-rc-sage hover:text-rc-forestlight transition-colors"
                  >
                    Enviar otra solicitud
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="diagnostic-form">
                  <h3 className="font-serif text-2xl text-stone-100">Agenda tu diagnóstico</h3>
                  <div>
                    <Label className="text-stone-300">Nombre completo *</Label>
                    <Input required data-testid="diag-nombre" value={form.nombre} onChange={set("nombre")}
                      className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
                  </div>
                  <div>
                    <Label className="text-stone-300">Correo electrónico *</Label>
                    <Input required type="email" data-testid="diag-correo" value={form.correo} onChange={set("correo")}
                      className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
                  </div>
                  <div>
                    <Label className="text-stone-300">Organización / Municipio</Label>
                    <Input data-testid="diag-organizacion" value={form.organizacion} onChange={set("organizacion")}
                      className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
                  </div>
                  <div>
                    <Label className="text-stone-300">¿Qué te interesa? *</Label>
                    <Select value={form.vertiente} onValueChange={(v) => setForm((f) => ({ ...f, vertiente: v }))}>
                      <SelectTrigger data-testid="diag-vertiente" className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100">
                        <SelectValue placeholder="Selecciona una opción" />
                      </SelectTrigger>
                      <SelectContent className="bg-rc-card border-rc-border text-stone-100">
                        {VERTIENTES.map((v) => (
                          <SelectItem key={v} value={v} className="focus:bg-rc-forest/30 focus:text-stone-100">{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-stone-300">Cuéntanos brevemente tu situación</Label>
                    <Textarea data-testid="diag-mensaje" value={form.mensaje} onChange={set("mensaje")} rows={4}
                      className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="diagnostic-submit-button"
                    className="w-full py-3.5 rounded-full bg-rc-forest hover:bg-rc-forestlight text-stone-100 transition-colors disabled:opacity-60"
                  >
                    {loading ? "Enviando…" : "Enviar solicitud"}
                  </button>
                  <p className="text-xs text-stone-600 text-center font-light">
                    Respondemos en máximo 48 horas hábiles. El diagnóstico inicial no tiene costo.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
};
